import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const TIMEOUT_MS = 20000;

// Maps-Key kommt zur Laufzeit aus maps-runtime.php (data-maps-key), nie aus dem Build
function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }

  return document.documentElement.dataset.mapsKey || "";
}

function isValidLatLng(lat, lng) {
  return typeof lat === "number" && typeof lng === "number" &&
    isFinite(lat) && isFinite(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180;
}

function str(v) {
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  if (typeof v === "string" && v.trim() !== "") {
    return v.trim();
  }

  return undefined;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.label) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature der Geocoder-API in das bestehende Geoaddress-Format.
  // Das Feature wird nicht verändert.
  _geoaddress(feature) {
    if (!feature || !feature.geometry || !Array.isArray(feature.geometry.coordinates)) {
      return null;
    }

    // GeoJSON: [lng, lat]
    const lng = Number(feature.geometry.coordinates[0]);
    const lat = Number(feature.geometry.coordinates[1]);
    if (!isValidLatLng(lat, lng)) {
      return null;
    }

    const p = feature.properties || {};
    const type = str(p.type);
    const name = str(p.name);

    let street = str(p.street);
    let number = str(p.housenumber);
    let city = str(p.city);
    let district = str(p.district);
    let label;

    if (type) {
      switch (type) {
        case "house":
          if (!street && name) {
            label = name;
          }
          break;
        case "street":
          street = street || name;
          number = undefined;
          break;
        case "city":
          city = name || city;
          break;
        case "district":
          district = name || district;
          break;
        default:
          // locality, county, state, country u. a.: Name als Beschriftung, nicht als Stadt
          if (name && name !== city && name !== district) {
            label = name;
          }
      }
    } else {
      // Legacy-Fallback über osm_key/osm_value
      const osmKey = str(p.osm_key);
      const osmValue = str(p.osm_value);

      if (osmKey === "highway") {
        street = street || name;
      } else if (osmKey === "place" && ["city", "town", "village", "municipality"].indexOf(osmValue) > -1) {
        city = name || city;
      } else if (osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb")) {
        if (city && name && name !== city) {
          district = name;
        } else if (name && !city) {
          label = name;
        }
      } else if (name && name !== city && name !== street) {
        label = name;
      }
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    const zip = str(p.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }
    if (label) {
      geoaddress["label"] = label;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(p.state);
    geoaddress["country"] = str(p.country);

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  _results(data) {
    const results = [];
    const features = data && Array.isArray(data.features) ? data.features : [];

    features.forEach(f => {
      const geoaddress = this._geoaddress(f);
      if (geoaddress) {
        results.push(geoaddress);
      }
    });

    return results;
  }

  // Liefert immer ein Promise, das endet: Treffer, [] bei Abbruch/ohne Key, oder Fehler.
  autocomplete(q) {
    this.cancelAutocomplete();

    const key = mapsKey();
    const query = typeof q === "string" ? q.trim() : "";
    if (!key || !query) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    const done = () => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    };

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: TIMEOUT_MS,
      params
    }).then(res => {
      done();
      return this._results(res.data);
    }, thrown => {
      done();
      if (this.axios.isCancel(thrown)) {
        return [];
      }
      throw thrown;
    });
  }

  // Liefert die Geoaddress oder null (kein Key, ungültige Koordinaten, kein Treffer, Fehler).
  reverse(lat, lng) {
    const key = mapsKey();
    const la = Number(lat);
    const lo = Number(lng);

    if (!key || !isValidLatLng(la, lo)) {
      return Promise.resolve(null);
    }

    const params = new URLSearchParams();
    params.append("lat", la);
    params.append("lon", lo);
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: TIMEOUT_MS,
      params
    }).then(res => {
      const results = this._results(res.data);
      return results.length > 0 ? results[0] : null;
    }, err => {
      console.warn("Reverse-Geocoding fehlgeschlagen", err && err.message); // eslint-disable-line no-console
      return null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
